import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import api from '../lib/api';

// Fallback content — used only if the API is unreachable, so the section
// never renders empty.
const DEFAULTS = {
  heading_lead: 'Why hello',
  heading_emph: 'there!',
  kicker: '(02) — About',
  location_tag: 'Berlin · Montréal',
  paragraphs: [
    'I enjoy journey maps, survey verbatims nobody else wants to read, and asking clients how a process made them feel, because how it made them feel is where the solution lies. Emotions are a good indicator of where the problem hides, and treating the root cause is better than a quick patch.',
    "I enjoy talking to people, and that means all kinds of people. Whether that's Customer Success Managers, Sales, Marketing, Support, Product, Solutions Architects, or Accounting, I enjoy the conversation with them all.",
    'Outside of work I run, bike, cook, dance badly on purpose — because why not be silly and childlike — and I collect languages the way other people collect Pokémon cards.',
  ],
  portrait_image:
    'https://customer-assets-4nw71qhi.emergentagent.net/job_creative-prowess/artifacts/56y8jzfy_2017-08-14_Profile%20Picture_Eileen.webp',
  portrait_alt: 'Eileen Tadeo, smiling in front of a red brick wall, arms crossed, gray blazer',
  portrait_caption: 'The operator · IRL',
  facts: [
    { label: 'Locations', lines: ['Berlin, DE', 'Montréal, CA', '(origin story)'] },
    { label: 'Experience', lines: ['10 years', 'across different', 'industries'] },
    { label: 'Superpower', lines: ['Superhuman connector,', 'chaos translator,', 'curiosity-driven'] },
    { label: 'Currently fighting', lines: ['German grammar,', 'B2 TELC.', "We're circling each other."] },
    { label: 'Currently building', lines: ['AI systems that turned my own', 'job search into a pipeline'] },
    { label: 'Spirit vegetable', lines: ['The ube. Purple,', 'grounded, and', 'surprisingly versatile.'] },
  ],
};

export default function About() {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [data, setData] = useState(DEFAULTS);

  useEffect(() => {
    api.get('/about')
      .then((r) => setData({ ...DEFAULTS, ...r.data }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const on = (e) => {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      setTilt({ x: (e.clientX - cx) / cx, y: (e.clientY - cy) / cy });
    };
    window.addEventListener('mousemove', on);
    return () => window.removeEventListener('mousemove', on);
  }, []);

  const facts = data.facts && data.facts.length ? data.facts : DEFAULTS.facts;
  const paragraphs = data.paragraphs && data.paragraphs.length ? data.paragraphs : DEFAULTS.paragraphs;

  return (
    <section id="about" className="relative py-28 md:py-40 bg-paper" data-testid="about-section">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="flex items-baseline justify-between mb-14">
          <div className="mono text-xs uppercase tracking-[0.3em] text-orchid">{data.kicker}</div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-ink/40 hidden md:block">{data.location_tag}</div>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="serif text-[12vw] md:text-[8vw] leading-[0.9] text-ink mb-16 md:mb-24"
        >
          {data.heading_lead} <span className="italic text-grape">{data.heading_emph}</span>
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Prose */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-7 space-y-6 mono text-[15px] md:text-[17px] text-ink/85 leading-[1.75]"
          >
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </motion.div>

          {/* Portrait */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1 }}
            className="lg:col-span-5 relative"
          >
            <div
              className="relative aspect-[4/5] overflow-hidden"
              style={{
                clipPath: 'polygon(0 6%, 100% 0, 96% 100%, 4% 96%)',
                transform: `perspective(1200px) rotateX(${tilt.y * -3}deg) rotateY(${tilt.x * 3}deg)`,
                transition: 'transform 200ms ease-out',
              }}
              data-testid="about-portrait"
            >
              <img
                src={data.portrait_image || DEFAULTS.portrait_image}
                alt={data.portrait_alt || DEFAULTS.portrait_alt}
                className="w-full h-full object-cover object-[center_top]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-grape/20 via-transparent to-orchid/25 mix-blend-multiply pointer-events-none" />
              <div className="absolute inset-0 pointer-events-none" style={{
                background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.15), transparent 55%)',
              }} />
            </div>
            {data.portrait_caption && (
              <div className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 bg-void text-paper mono text-[10px] uppercase tracking-[0.3em] px-4 py-2">
                {data.portrait_caption}
              </div>
            )}
          </motion.div>
        </div>

        {/* Facts grid */}
        <div className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
          {facts.map((f, i) => (
            <motion.div
              key={`${f.label}-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="bg-paper p-8 md:p-10 group hover:bg-lilac/20 transition-colors"
              data-testid={`fact-${String(f.label).toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-orchid mb-6">{String(i + 1).padStart(2, '0')} / {f.label}</div>
              <div className="serif text-2xl md:text-3xl text-ink leading-tight">
                {(f.lines || []).map((l, k) => (
                  <div key={k}>{l}</div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
